import React, { useState, useEffect } from 'react'
import {
  BookOpen, ChevronRight, CheckCircle, Lightbulb,
  Target, Brain, ArrowRight, Star, AlertTriangle,
  FileText, PenTool, Youtube,
  ChevronDown, X,
  MessageCircle, Sparkles
} from 'lucide-react'
import { useProgressStore, useLearnProgress, useUnitProgress } from '../store/progressStore'
import { useVideoStore } from '../store/videoStore'
import ConceptVideoModal from '../components/ConceptVideoModal'
import {
  computePredictedFragments, FRAGMENT_TAG_CONFIG,
  type PredictedFragment
} from '../store/predictedFragments'

const UNIT_ID = 'G1-2'

// ── 스토리텔링 인트로 데이터 ─────────────────────────────────────────────────
interface StoryLine {
  speaker: 'student' | 'teacher' | 'friend' | 'narrator'
  emoji: string
  name: string
  text: string
}
interface StepStory {
  scene: string
  lines: StoryLine[]
  reveal: string
  revealEmoji: string
}

const STEP_STORIES: Record<string, StepStory> = {
  GB01: {
    scene: '🎨 미술 수업. 타일 패턴을 디자인하는 중.',
    lines: [
      { speaker: 'student', emoji: '🙋', name: '지호', text: '선생님, 정육각형 타일은 어떻게 딱 맞게 붙이나요? 각도가 얼마인지 알아야 하지 않나요?' },
      { speaker: 'teacher', emoji: '👩‍🏫', name: '선생님', text: '좋은 질문이에요! n각형 내각의 합은 (n-2)×180°예요. 정육각형이면 (6-2)×180°=720°이고, 한 내각은 720°÷6=120°죠.' },
      { speaker: 'friend', emoji: '😮', name: '수아', text: '그래서 정육각형 타일 3개가 한 점에 모이면 120°×3=360°가 되어 딱 맞는구나!' },
      { speaker: 'teacher', emoji: '👩‍🏫', name: '선생님', text: '맞아요! 외각은 내각의 보각이에요. n각형 외각의 합은 항상 360°랍니다!' },
      { speaker: 'narrator', emoji: '💡', name: '', text: '내각의 합 = (n-2)×180° | 정n각형 한 내각 = (n-2)×180°÷n | 외각의 합 = 360°' },
    ],
    reveal: '내각의 합: (n-2)×180° | 외각의 합: 항상 360°',
    revealEmoji: '🔷',
  },
  GB02: {
    scene: '🏗️ 건축 현장. 지붕 트러스 구조물을 설계 중.',
    lines: [
      { speaker: 'student', emoji: '🙋', name: '지호', text: '삼각형은 왜 이렇게 많이 쓰나요? 지붕도 삼각형이고, 다리도 삼각형이고...' },
      { speaker: 'teacher', emoji: '👷', name: '설계사', text: '삼각형은 세 변의 길이가 정해지면 모양이 딱 하나로 결정되기 때문에 가장 안정적이에요! 이걸 "삼각형의 결정 조건"이라고 해요.' },
      { speaker: 'friend', emoji: '🤔', name: '수아', text: '이등변삼각형은 두 변이 같은 거잖아요. 그러면 두 밑각도 같아요?' },
      { speaker: 'teacher', emoji: '👷', name: '설계사', text: '정확해요! 이등변삼각형의 두 밑각은 서로 같아요. 정삼각형은 세 각이 모두 60°이고요.' },
      { speaker: 'narrator', emoji: '💡', name: '', text: '이등변삼각형: 두 밑각 같음 | 정삼각형: 모든 각 60° | 직각삼각형: 한 각이 90°' },
    ],
    reveal: '이등변삼각형 밑각=같음 | 정삼각형 각=60° | 삼각형 내각 합=180°',
    revealEmoji: '📐',
  },
  GB03: {
    scene: '⏰ 시계 제조 공장. 원형 부품을 만드는 중.',
    lines: [
      { speaker: 'student', emoji: '🙋', name: '지호', text: '시계 바늘이 12에서 3까지 움직이면 호의 길이는 어떻게 구해요?' },
      { speaker: 'teacher', emoji: '🔧', name: '기술자', text: '부채꼴의 호 길이는 반지름과 중심각에 비례해요! l = 2πr × (θ/360°) 공식을 써요.' },
      { speaker: 'friend', emoji: '😄', name: '수아', text: '그럼 넓이도 비슷하게 구해요?' },
      { speaker: 'teacher', emoji: '🔧', name: '기술자', text: '네! 부채꼴 넓이 = πr² × (θ/360°)예요. 또는 S = ½lr로도 구할 수 있어요.' },
      { speaker: 'narrator', emoji: '💡', name: '', text: '호 길이 l = 2πr×(θ/360°) | 넓이 S = πr²×(θ/360°) = ½lr' },
    ],
    reveal: '호 길이: l=2πr×θ/360° | 넓이: S=πr²×θ/360° = ½lr',
    revealEmoji: '🥧',
  },
  GB04: {
    scene: '🌏 지구과학 수업. 지구 둘레를 계산하는 실험.',
    lines: [
      { speaker: 'student', emoji: '🙋', name: '지호', text: '에라토스테네스가 지구 둘레를 어떻게 계산했대요?' },
      { speaker: 'teacher', emoji: '👩‍🏫', name: '선생님', text: '원의 둘레 공식 C=2πr을 알았기 때문이에요! 두 도시의 그림자 각도 차이로 호의 비율을 구하고, 그걸 360°로 환산했어요.' },
      { speaker: 'friend', emoji: '🤩', name: '수아', text: '원의 넓이는 πr²이죠? 반지름이 두 배가 되면 넓이는 4배가 되는 거 아닌가요?' },
      { speaker: 'teacher', emoji: '👩‍🏫', name: '선생님', text: '정확해요! 반지름이 2배→ 넓이 4배. 이게 r²의 의미예요. 둘레는 2배, 넓이는 4배!' },
      { speaker: 'narrator', emoji: '💡', name: '', text: '원 둘레: C=2πr=πd | 원 넓이: A=πr² | π≈3.14159' },
    ],
    reveal: '원 둘레: C=2πr | 원 넓이: A=πr²',
    revealEmoji: '⭕',
  },
}

// ── 학습 단계 정의 ───────────────────────────────────────────────────────────
const STEPS = [
  {
    id: 'GB01',
    title: '다각형의 내각·외각의 합',
    subtitle: 'n각형 내각의 합 공식과 외각의 합 360°를 이해한다',
    icon: '🔷',
    color: 'bg-emerald-500',
    lightColor: 'bg-emerald-50',
    borderColor: 'border-emerald-300',
    textColor: 'text-emerald-700',
    difficulty: '기초',
    difficultyColor: 'bg-green-100 text-green-700',
    tags: ['다각형', '내각', '외각', '정다각형'],
    objective: 'n각형의 내각의 합이 (n-2)×180°임을 이해하고, 외각의 합이 항상 360°임을 설명할 수 있다.',
    concept: `**다각형의 내각·외각의 합**

📌 내각의 합
• n각형을 삼각형으로 분할하면 (n-2)개의 삼각형이 생김
• 삼각형 1개의 내각 합 = 180°
• n각형 내각의 합 = (n-2) × 180°

| n각형 | 내각의 합 |
|-------|----------|
| 삼각형(3) | 180° |
| 사각형(4) | 360° |
| 오각형(5) | 540° |
| 육각형(6) | 720° |
| n각형 | (n-2)×180° |

📌 정n각형 한 내각의 크기
• 한 내각 = (n-2)×180° ÷ n

📌 외각의 합
• 내각과 외각의 합 = 180° (직선각)
• n각형 외각의 합 = 360° (항상!)
• 정n각형 한 외각 = 360° ÷ n

📌 외각의 합이 360°인 이유
• 다각형을 한 바퀴 돌면 360° 회전
• n각형 외각의 합 = 360° (n에 무관!)

⚠️ 주의
• 외각의 합은 항상 360° (n에 상관없이!)
• 내각의 크기와 외각의 크기 합 = 180°`,
    keyPoints: [
      'n각형 내각의 합 = (n-2) × 180°',
      '정n각형 한 내각 = (n-2)×180° ÷ n',
      '모든 다각형 외각의 합 = 360°',
      '정n각형 한 외각 = 360° ÷ n',
    ],
    examples: [
      {
        question: '다음을 구하시오.\n① 정오각형의 내각의 합\n② 정오각형 한 내각의 크기\n③ 정오각형 한 외각의 크기',
        answer: '① 540°  ② 108°  ③ 72°',
        explanation: '① (5-2)×180° = 3×180° = 540°\n② 540°÷5 = 108°\n③ 360°÷5 = 72° (또는 180°-108°=72°)',
      },
      {
        question: '내각의 합이 1440°인 다각형은 몇 각형인가?',
        answer: '십각형(10각형)',
        explanation: '(n-2)×180° = 1440°\nn-2 = 8\nn = 10 → 십각형 ✓',
      },
    ],
    practice: {
      question: '정팔각형에 대해 구하시오.\n① 내각의 합\n② 한 내각의 크기\n③ 한 외각의 크기',
      answer: '① 1080°  ② 135°  ③ 45°',
      hint: '① (n-2)×180°에 n=8 대입. ③ 360°÷8 또는 180°-135°',
      steps: [
        '① n=8: (8-2)×180° = 6×180° = 1080°',
        '② 1080°÷8 = 135°',
        '③ 360°÷8 = 45° (또는 180°-135° = 45°)',
      ],
    },
    misconceptions: ['GP01'],
  },
  {
    id: 'GB02',
    title: '삼각형의 종류와 성질',
    subtitle: '삼각형을 변과 각의 크기로 분류하고 이등변삼각형의 성질을 이해한다',
    icon: '📐',
    color: 'bg-orange-500',
    lightColor: 'bg-orange-50',
    borderColor: 'border-orange-300',
    textColor: 'text-orange-700',
    difficulty: '기초',
    difficultyColor: 'bg-green-100 text-green-700',
    tags: ['이등변삼각형', '정삼각형', '직각삼각형', '삼각형'],
    objective: '삼각형을 변의 길이와 각의 크기로 분류하고, 이등변삼각형의 성질(두 밑각이 같음)을 활용한다.',
    concept: `**삼각형의 종류와 성질**

📌 변의 길이에 따른 분류
| 종류 | 특징 |
|------|------|
| 부등변삼각형 | 세 변의 길이 모두 다름 |
| 이등변삼각형 | 두 변의 길이가 같음 |
| 정삼각형 | 세 변의 길이 모두 같음 |

📌 각의 크기에 따른 분류
| 종류 | 특징 |
|------|------|
| 예각삼각형 | 세 각 모두 예각 |
| 직각삼각형 | 한 각이 90° |
| 둔각삼각형 | 한 각이 둔각 |

📌 이등변삼각형의 성질
• 두 변의 길이가 같음 (등변)
• 두 밑각의 크기가 같음 ★
• 꼭짓각의 이등분선 = 밑변의 수직이등분선

📌 정삼각형의 성질
• 세 변의 길이 모두 같음
• 세 각 모두 60°
• 정삼각형은 이등변삼각형의 특수한 경우

📌 삼각형의 성질
• 내각의 합 = 180°
• 한 외각 = 나머지 두 내각의 합
• 삼각형의 결정 조건:
  - 세 변의 길이가 주어질 때
  - 두 변의 길이와 끼인각
  - 한 변의 길이와 양 끝각

⚠️ 삼각형 성립 조건
• 가장 긴 변 < 나머지 두 변의 합`,
    keyPoints: [
      '이등변삼각형: 두 밑각의 크기가 같다',
      '정삼각형: 세 각이 모두 60°',
      '삼각형 내각의 합 = 180°',
      '한 외각 = 나머지 두 내각의 합',
    ],
    examples: [
      {
        question: '이등변삼각형의 꼭짓각이 50°일 때\n① 두 밑각의 크기를 각각 구하시오.\n② 이 삼각형은 예각·직각·둔각 중 어느 삼각형인가?',
        answer: '① 각각 65°  ② 예각삼각형',
        explanation: '① 밑각=(180°-50°)÷2=130°÷2=65°\n② 세 각이 50°, 65°, 65° 모두 90° 미만 → 예각삼각형',
      },
      {
        question: '삼각형의 한 외각이 120°이고\n이웃하는 한 내각이 50°일 때\n나머지 두 내각의 크기를 구하시오.',
        answer: '70°, 50°, 60°',
        explanation: '외각 = 나머지 두 내각의 합\n→ 120° = 50° + x → x = 70°\n세 내각: 50°, 70°, 60° (합=180° ✓)',
      },
    ],
    practice: {
      question: '이등변삼각형 ABC에서 AB=AC이다.\n∠B = 3x+5°, ∠C = 5x-15°일 때\n① x의 값을 구하시오.\n② 세 내각의 크기를 각각 구하시오.',
      answer: '① x=10  ② ∠B=∠C=35°, ∠A=110°',
      hint: 'AB=AC이면 밑각 ∠B=∠C. 방정식 세우기!',
      steps: [
        '① ∠B = ∠C이므로 3x+5 = 5x-15',
        '20 = 2x → x = 10',
        '② ∠B = 3×10+5 = 35°, ∠C = 35°',
        '∠A = 180°-35°-35° = 110°',
      ],
    },
    misconceptions: ['GP02'],
  },
  {
    id: 'GB03',
    title: '부채꼴의 호와 넓이',
    subtitle: '부채꼴의 호 길이와 넓이를 중심각과 반지름으로 구한다',
    icon: '🥧',
    color: 'bg-violet-500',
    lightColor: 'bg-violet-50',
    borderColor: 'border-violet-300',
    textColor: 'text-violet-700',
    difficulty: '중급',
    difficultyColor: 'bg-yellow-100 text-yellow-700',
    tags: ['부채꼴', '호', '중심각', '넓이'],
    objective: '부채꼴의 호 길이 l=2πr×(θ/360°)와 넓이 S=πr²×(θ/360°)=½lr를 이해하고 계산할 수 있다.',
    concept: `**부채꼴의 호와 넓이**

📌 부채꼴이란?
• 원에서 두 반지름과 호로 이루어진 도형
• 중심각 θ에 따라 크기 결정

📌 호의 길이 (l)
• l = 2πr × (θ/360°)
• 호의 길이는 중심각에 비례

📌 부채꼴의 넓이 (S)
• S = πr² × (θ/360°)
• 또는 S = ½lr (반지름과 호 길이로 계산)

📌 비례 관계
• 같은 원에서: 호의 길이 ∝ 중심각
• 같은 원에서: 넓이 ∝ 중심각

📌 예시 계산 (r=6cm, θ=120°)
• 호 길이: l = 2π×6×(120/360) = 12π×(1/3) = 4π cm
• 넓이: S = π×36×(1/3) = 12π cm²
• 검산: S = ½×4π×6 = 12π cm² ✓

📌 원과의 비교
| 도형 | 둘레/호 | 넓이 |
|------|---------|------|
| 원(θ=360°) | 2πr | πr² |
| 부채꼴 | 2πr×(θ/360°) | πr²×(θ/360°) |

⚠️ 주의
• 부채꼴의 둘레 = 호의 길이 + 반지름 × 2
• 호의 길이만 구할 때와 둘레 구할 때 구분!`,
    keyPoints: [
      '호 길이 l = 2πr × θ/360°',
      '넓이 S = πr² × θ/360° = ½lr',
      '호·넓이 모두 중심각에 비례',
      '부채꼴 둘레 = l + 2r (호+양쪽 반지름)',
    ],
    examples: [
      {
        question: '반지름 9cm, 중심각 80°인 부채꼴의\n① 호의 길이\n② 넓이\n③ 둘레',
        answer: '① 4π cm  ② 18π cm²  ③ (4π+18) cm',
        explanation: '① l=2π×9×(80/360)=18π×2/9=4π cm\n② S=π×81×(80/360)=81π×2/9=18π cm²\n③ 둘레=4π+9+9=4π+18 cm',
      },
      {
        question: '호의 길이가 6π cm이고 반지름이 9cm인 부채꼴의 중심각과 넓이를 구하시오.',
        answer: '중심각=120°, 넓이=27π cm²',
        explanation: '6π=2π×9×(θ/360°) → θ=120°\nS=½×6π×9=27π cm²',
      },
    ],
    practice: {
      question: '반지름 12cm, 중심각 150°인 부채꼴에 대해\n① 호의 길이를 구하시오.\n② 넓이를 구하시오.\n③ 둘레를 구하시오.',
      answer: '① 10π cm  ② 60π cm²  ③ (10π+24) cm',
      hint: 'θ=150°=5/12×360°. 호 길이: 2π×12×5/12. 넓이: ½×호×r',
      steps: [
        '① l=2π×12×(150/360)=24π×(5/12)=10π cm',
        '② S=½×10π×12=60π cm²',
        '③ 둘레=10π+12+12=10π+24 cm',
      ],
    },
    misconceptions: ['GP03'],
  },
  {
    id: 'GB04',
    title: '원의 둘레와 넓이',
    subtitle: '원의 둘레와 넓이 공식을 이해하고 활용한다',
    icon: '⭕',
    color: 'bg-teal-500',
    lightColor: 'bg-teal-50',
    borderColor: 'border-teal-300',
    textColor: 'text-teal-700',
    difficulty: '기초',
    difficultyColor: 'bg-green-100 text-green-700',
    tags: ['원', '둘레', '넓이', 'π'],
    objective: '원의 둘레 C=2πr과 넓이 A=πr²을 이해하고, 반지름과의 관계를 설명할 수 있다.',
    concept: `**원의 둘레와 넓이**

📌 원의 기본 용어
• 중심(O): 원의 가운데 점
• 반지름(r): 중심에서 원 위의 점까지 거리
• 지름(d): 원을 지나는 가장 긴 선분, d=2r
• 원주율(π): 둘레÷지름 = 약 3.14159...

📌 원의 둘레 (원주)
• C = 2πr = πd
• 반지름이 2배 → 둘레 2배

📌 원의 넓이
• A = πr²
• 반지름이 2배 → 넓이 4배 (r² 때문)

📌 반지름별 비교표
| r | 둘레(2πr) | 넓이(πr²) |
|---|----------|----------|
| 1 | 2π | π |
| 2 | 4π | 4π |
| 3 | 6π | 9π |
| 4 | 8π | 16π |
| 5 | 10π | 25π |

📌 색칠한 부분의 넓이 계산 전략
① 전체 원 넓이 - 제외할 부분
② 부채꼴 공식 활용
③ 대칭성 이용

⚠️ π 계산 주의
• π = 3.14로 근사 또는 π 그대로 답
• 문제에서 특별히 언급 없으면 π로 표현`,
    keyPoints: [
      '원의 둘레: C = 2πr (반지름의 2π배)',
      '원의 넓이: A = πr² (반지름의 제곱×π)',
      '반지름 2배 → 둘레 2배, 넓이 4배',
      '지름 d = 2r, 원주율 π ≈ 3.14',
    ],
    examples: [
      {
        question: '반지름이 7cm인 원의\n① 둘레와 ② 넓이를 구하시오. (π 사용)',
        answer: '① 14π cm  ② 49π cm²',
        explanation: '① C=2πr=2π×7=14π cm\n② A=πr²=π×49=49π cm²',
      },
      {
        question: '넓이가 36π cm²인 원의 반지름과 둘레를 구하시오.',
        answer: '반지름=6cm, 둘레=12π cm',
        explanation: 'πr²=36π → r²=36 → r=6cm\nC=2π×6=12π cm',
      },
    ],
    practice: {
      question: '큰 원의 반지름이 8cm이고\n그 안에 반지름 3cm인 작은 원이 있다.\n색칠한 부분(큰 원 - 작은 원)의 넓이를 구하시오.',
      answer: '55π cm²',
      hint: '큰 원 넓이 - 작은 원 넓이. 각각 πr² 공식 사용.',
      steps: [
        '큰 원 넓이: π×8² = 64π cm²',
        '작은 원 넓이: π×3² = 9π cm²',
        '색칠한 부분: 64π - 9π = 55π cm²',
      ],
    },
    misconceptions: ['GP04'],
  },
]

// ── 하위 컴포넌트 ────────────────────────────────────────────────────────────
function PracticePanel({ practice }: { practice: typeof STEPS[0]['practice'] }) {
  const [showHint, setShowHint] = useState(false)
  const [showAnswer, setShowAnswer] = useState(false)
  const [showSteps, setShowSteps] = useState(false)
  return (
    <div className="mt-4 bg-emerald-50 border border-emerald-200 rounded-2xl p-4">
      <p className="text-xs font-bold text-emerald-600 mb-2 flex items-center gap-1">
        <PenTool size={12} /> 연습 문제
      </p>
      <p className="text-sm text-slate-800 font-medium mb-3 whitespace-pre-line">{practice.question}</p>
      <div className="flex flex-wrap gap-2">
        <button onClick={() => setShowHint(!showHint)}
          className="text-xs px-3 py-1.5 rounded-lg bg-amber-100 text-amber-700 hover:bg-amber-200 font-semibold transition-colors">
          💡 힌트
        </button>
        <button onClick={() => setShowSteps(!showSteps)}
          className="text-xs px-3 py-1.5 rounded-lg bg-blue-100 text-blue-700 hover:bg-blue-200 font-semibold transition-colors">
          📋 풀이 단계
        </button>
        <button onClick={() => setShowAnswer(!showAnswer)}
          className="text-xs px-3 py-1.5 rounded-lg bg-emerald-100 text-emerald-700 hover:bg-emerald-200 font-semibold transition-colors">
          ✅ 정답 확인
        </button>
      </div>
      {showHint && (
        <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-800">
          💡 {practice.hint}
        </div>
      )}
      {showSteps && (
        <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-xl">
          <p className="text-xs font-bold text-blue-700 mb-2">단계별 풀이:</p>
          {practice.steps.map((s, i) => (
            <div key={i} className="flex gap-2 text-xs text-blue-800 mb-1">
              <span className="font-bold text-blue-500 w-4">{i + 1}.</span>
              <span className="font-mono">{s}</span>
            </div>
          ))}
        </div>
      )}
      {showAnswer && (
        <div className="mt-3 p-3 bg-emerald-50 border border-emerald-200 rounded-xl text-xs text-emerald-800 font-mono whitespace-pre-line">
          {practice.answer}
        </div>
      )}
    </div>
  )
}

function ExampleCard({ example, idx }: { example: typeof STEPS[0]['examples'][0], idx: number }) {
  const [open, setOpen] = useState(false)
  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden">
      <button onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 hover:bg-slate-100 transition-colors text-left">
        <span className="text-sm font-semibold text-slate-700">예제 {idx + 1}: {example.question.slice(0, 28)}...</span>
        <ChevronDown size={14} className={`text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="px-4 py-3 space-y-2">
          <p className="text-sm text-slate-800 whitespace-pre-line">{example.question}</p>
          <div className="bg-emerald-50 rounded-lg p-3">
            <p className="text-xs font-bold text-emerald-600 mb-1">풀이</p>
            <p className="text-sm font-mono text-emerald-800 whitespace-pre-line">{example.answer}</p>
          </div>
          <p className="text-xs text-slate-500 italic whitespace-pre-line">{example.explanation}</p>
        </div>
      )}
    </div>
  )
}

function StoryCard({ story }: { story: StepStory }) {
  const [lineIdx, setLineIdx] = useState(0)
  const [revealed, setRevealed] = useState(false)
  const speakerColor: Record<string, string> = {
    student: 'bg-emerald-100 text-emerald-800',
    teacher: 'bg-teal-100 text-teal-800',
    friend: 'bg-green-100 text-green-800',
    narrator: 'bg-amber-100 text-amber-800',
  }
  return (
    <div className="bg-gradient-to-b from-emerald-50 to-white border border-emerald-200 rounded-2xl p-4">
      <p className="text-xs text-slate-500 italic mb-3">{story.scene}</p>
      <div className="min-h-[80px] mb-3">
        {story.lines.slice(0, lineIdx + 1).map((line, i) => (
          <div key={i} className={`flex gap-2 mb-2 ${i < lineIdx ? 'opacity-50' : ''}`}>
            <span className="text-lg flex-shrink-0">{line.emoji}</span>
            <div>
              {line.name && (
                <span className={`text-xs font-bold px-1.5 py-0.5 rounded mr-1 ${speakerColor[line.speaker]}`}>
                  {line.name}
                </span>
              )}
              <span className="text-sm text-slate-700">{line.text}</span>
            </div>
          </div>
        ))}
      </div>
      <div className="flex items-center gap-2 mb-3">
        <div className="flex-1 h-1 bg-emerald-100 rounded-full">
          <div className="h-1 bg-emerald-400 rounded-full transition-all"
            style={{ width: `${((lineIdx + 1) / story.lines.length) * 100}%` }} />
        </div>
        <span className="text-xs text-slate-400">{lineIdx + 1}/{story.lines.length}</span>
      </div>
      {!revealed ? (
        <div className="flex gap-2">
          {lineIdx < story.lines.length - 1 && (
            <button onClick={() => setLineIdx(lineIdx + 1)}
              className="flex-1 py-2 bg-emerald-100 hover:bg-emerald-200 text-emerald-700 font-semibold rounded-xl text-xs transition-colors">
              다음 대화 →
            </button>
          )}
          {lineIdx === story.lines.length - 1 && (
            <button onClick={() => setRevealed(true)}
              className="flex-1 py-2 bg-emerald-500 hover:bg-emerald-600 text-white font-bold rounded-xl text-xs transition-colors flex items-center justify-center gap-1">
              <Sparkles size={12} /> 핵심 공식 보기
            </button>
          )}
        </div>
      ) : (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3">
          <p className="text-lg text-center mb-1">{story.revealEmoji}</p>
          <p className="text-xs text-emerald-800 font-mono text-center">{story.reveal}</p>
        </div>
      )}
    </div>
  )
}

function PredictedFragmentPanel({ fragments }: { fragments: PredictedFragment[] }) {
  const [dismissed, setDismissed] = useState<Set<string>>(new Set())
  const visible = fragments.filter(f => !dismissed.has(f.fragment.id)).slice(0, 3)
  if (visible.length === 0) return null
  return (
    <div className="mb-4 card border border-amber-200 bg-amber-50">
      <p className="text-xs font-bold text-amber-700 mb-2 flex items-center gap-1">
        <Brain size={12} /> AI 예상 취약 개념
      </p>
      <div className="space-y-1.5">
        {visible.map(f => {
          const cfg = FRAGMENT_TAG_CONFIG[f.tag] ?? FRAGMENT_TAG_CONFIG['review']
          return (
            <div key={f.fragment.id} className={`flex items-center gap-2 p-2 rounded-lg border ${cfg.color}`}>
              <span>{f.fragment.emoji}</span>
              <div className="flex-1 min-w-0">
                <p className={`text-xs font-semibold truncate ${cfg.color}`}>{f.fragment.title}</p>
                <p className="text-xs text-slate-500 truncate">{f.fragment.fixTip.slice(0, 40)}...</p>
              </div>
              <button onClick={() => setDismissed(s => new Set([...s, f.fragment.id]))}
                className="text-slate-300 hover:text-slate-500 flex-shrink-0"><X size={11} /></button>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ── 메인 컴포넌트 ────────────────────────────────────────────────────────────
const TOTAL_STEPS = STEPS.length

interface G1_2LearnPageProps { defaultStepId?: string | null }

export default function G1_2LearnPage({ defaultStepId }: G1_2LearnPageProps) {
  const [selectedStep, setSelectedStep] = useState<typeof STEPS[0] | null>(null)
  const [activeTab, setActiveTab] = useState<'story' | 'concept' | 'examples' | 'practice'>('story')

  const { completeStep } = useProgressStore(s => ({ completeStep: s.completeStep }))
  const learnProgress = useLearnProgress(UNIT_ID)
  const unitProgress = useUnitProgress(UNIT_ID)
  const predictedFragments = computePredictedFragments(unitProgress, 3)
  const getApprovedByTag = useVideoStore(s => s.getApprovedByTag)
  const [videoModal, setVideoModal] = useState<{ stepId: string; label: string } | null>(null)

  const completedCount = learnProgress.completedCount
  const progressPct = learnProgress.pct

  useEffect(() => {
    if (defaultStepId) {
      const step = STEPS.find(s => s.id === defaultStepId)
      if (step) { setSelectedStep(step); setActiveTab('concept') }
    }
  }, [defaultStepId])

  const handleComplete = (step: typeof STEPS[0]) => {
    completeStep(UNIT_ID, step.id, step.title)
    setSelectedStep(null)
  }

  // ── 단계 목록 화면 ──────────────────────────────────────────────────────────
  if (!selectedStep) {
    return (
      <div className="p-4 space-y-4">
        <PredictedFragmentPanel fragments={predictedFragments} />

        {/* 헤더 */}
        <div className="card bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-12 h-12 bg-emerald-100 rounded-2xl flex items-center justify-center text-2xl">🔷</div>
            <div>
              <h2 className="text-lg font-black text-slate-800">평면도형</h2>
              <p className="text-xs text-slate-500">G1-2 · 도형 영역</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex-1 h-2 bg-emerald-100 rounded-full overflow-hidden">
              <div className="h-2 bg-emerald-500 rounded-full transition-all"
                style={{ width: `${progressPct}%` }} />
            </div>
            <span className="text-xs font-bold text-emerald-600 w-16 text-right">
              {completedCount}/{TOTAL_STEPS} 완료
            </span>
          </div>
        </div>

        {/* 단계 목록 */}
        <div className="space-y-2">
          {STEPS.map((step) => {
            const stepProg = learnProgress.steps[step.id]
            const done = stepProg?.status === 'completed' || stepProg?.status === 'mastered'
            const video = getApprovedByTag(step.id)
            return (
              <div key={step.id}
                className={`card border-2 cursor-pointer transition-all hover:shadow-md ${done ? 'border-emerald-200 bg-emerald-50/30' : 'border-slate-200 hover:border-emerald-300'}`}
                onClick={() => { setSelectedStep(step); setActiveTab('story') }}>
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-lg flex-shrink-0 ${done ? 'bg-emerald-100' : step.lightColor}`}>
                    {done ? '✅' : step.icon}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-bold text-slate-800">{step.title}</span>
                      <span className={`text-xs px-1.5 py-0.5 rounded-full font-semibold ${step.difficultyColor}`}>
                        {step.difficulty}
                      </span>
                      {video && (
                        <span className="text-xs px-1.5 py-0.5 rounded-full bg-red-100 text-red-600 font-semibold flex items-center gap-0.5">
                          <Youtube size={9} /> 영상
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-slate-500 truncate">{step.subtitle}</p>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <span className="text-xs text-slate-400">{step.id}</span>
                    <ChevronRight size={15} className="text-slate-300" />
                  </div>
                </div>
              </div>
            )
          })}
        </div>

        {completedCount === TOTAL_STEPS && (
          <div className="card bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-300 text-center">
            <div className="text-3xl mb-2">🎉</div>
            <p className="text-sm font-bold text-emerald-700">모든 개념 학습 완료!</p>
            <p className="text-xs text-slate-500 mt-1">실전 TEST에 도전해보세요</p>
          </div>
        )}
      </div>
    )
  }

  // ── 단계 상세 화면 ──────────────────────────────────────────────────────────
  const step = selectedStep
  const stepProg = learnProgress.steps[step.id]
  const done = stepProg?.status === 'completed' || stepProg?.status === 'mastered'
  const story = STEP_STORIES[step.id]
  const video = getApprovedByTag(step.id)

  const tabs = [
    { id: 'story' as const, label: '스토리', icon: <MessageCircle size={13} /> },
    { id: 'concept' as const, label: '개념', icon: <BookOpen size={13} /> },
    { id: 'examples' as const, label: '예제', icon: <FileText size={13} /> },
    { id: 'practice' as const, label: '연습', icon: <PenTool size={13} /> },
  ]

  return (
    <div className="flex flex-col h-full">
      {/* 상단 헤더 */}
      <div className={`${step.lightColor} border-b ${step.borderColor} px-4 pt-3 pb-0 flex-shrink-0`}>
        <button onClick={() => setSelectedStep(null)}
          className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700 mb-2 transition-colors">
          ← 목록으로
        </button>
        <div className="flex items-center gap-2 mb-3">
          <span className="text-2xl">{step.icon}</span>
          <div>
            <h2 className="text-base font-black text-slate-800">{step.title}</h2>
            <p className="text-xs text-slate-500">{step.subtitle}</p>
          </div>
          {done && <CheckCircle size={18} className="text-emerald-500 ml-auto" />}
        </div>
        {/* 태그 */}
        <div className="flex gap-1 flex-wrap mb-2">
          {step.tags.map(tag => (
            <span key={tag} className="text-xs px-2 py-0.5 bg-white/80 border border-emerald-200 text-emerald-700 rounded-full">
              #{tag}
            </span>
          ))}
        </div>
        {/* 탭 */}
        <div className="flex gap-1 overflow-x-auto">
          {tabs.map(t => (
            <button key={t.id} onClick={() => setActiveTab(t.id)}
              className={`flex items-center gap-1 px-3 py-2 rounded-t-lg text-xs font-semibold transition-colors whitespace-nowrap ${
                activeTab === t.id
                  ? 'bg-white text-emerald-700 border-t border-l border-r border-emerald-200'
                  : 'text-slate-500 hover:text-slate-700'
              }`}>
              {t.icon}{t.label}
            </button>
          ))}
        </div>
      </div>

      {/* 콘텐츠 영역 */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {activeTab === 'story' && story && <StoryCard story={story} />}

        {activeTab === 'concept' && (
          <div className="space-y-4">
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3">
              <p className="text-xs font-bold text-emerald-700 mb-1 flex items-center gap-1">
                <Target size={11} /> 학습 목표
              </p>
              <p className="text-sm text-slate-700">{step.objective}</p>
            </div>
            <div className="card">
              <p className="text-xs font-bold text-slate-500 mb-2 flex items-center gap-1">
                <BookOpen size={11} /> 핵심 개념
              </p>
              <div className="text-sm text-slate-700 whitespace-pre-line leading-relaxed">
                {step.concept}
              </div>
            </div>
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3">
              <p className="text-xs font-bold text-emerald-700 mb-2 flex items-center gap-1">
                <Star size={11} /> 핵심 포인트
              </p>
              <ul className="space-y-1.5">
                {step.keyPoints.map((pt, i) => (
                  <li key={i} className="flex items-start gap-2 text-xs text-slate-700">
                    <span className="text-emerald-500 font-bold flex-shrink-0">✓</span>
                    {pt}
                  </li>
                ))}
              </ul>
            </div>
            {video && (
              <button onClick={() => setVideoModal({ stepId: step.id, label: step.title })}
                className="w-full flex items-center gap-2 px-4 py-3 bg-red-50 border border-red-200 rounded-xl hover:bg-red-100 transition-colors">
                <Youtube size={16} className="text-red-600" />
                <span className="text-sm font-semibold text-red-700">개념 설명 영상 보기</span>
                <ArrowRight size={13} className="text-red-400 ml-auto" />
              </button>
            )}
          </div>
        )}

        {activeTab === 'examples' && (
          <div className="space-y-3">
            <p className="text-xs font-bold text-slate-500">📝 예제 문제</p>
            {step.examples.map((ex, i) => (
              <ExampleCard key={i} example={ex} idx={i} />
            ))}
          </div>
        )}

        {activeTab === 'practice' && (
          <PracticePanel practice={step.practice} />
        )}

        {/* 완료 버튼 */}
        {!done ? (
          <button onClick={() => handleComplete(step)}
            className="w-full py-3 bg-emerald-500 hover:bg-emerald-600 text-white font-black rounded-2xl transition-colors flex items-center justify-center gap-2">
            <CheckCircle size={16} /> 학습 완료
          </button>
        ) : (
          <div className="flex items-center justify-center gap-2 py-3 bg-emerald-50 border border-emerald-200 rounded-2xl">
            <CheckCircle size={16} className="text-emerald-500" />
            <span className="text-sm font-bold text-emerald-700">완료된 단계입니다</span>
          </div>
        )}
      </div>

      {videoModal && (
        <ConceptVideoModal
          conceptTag={videoModal.stepId}
          conceptLabel={videoModal.label}
          onClose={() => setVideoModal(null)}
        />
      )}
    </div>
  )
}
